import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Wrench, Plus, Pencil, Trash2, Code, Search, Settings, ImageIcon, X } from "lucide-react";

export const ToolsManagement = () => {
  const [tools, setTools] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [formData, setFormData] = useState({ 
    name: "", 
    description: "", 
    icon: "wrench",
    icon_url: "",
    parser_config: null
  });
  const [parserConfig, setParserConfig] = useState({
    required_path_pattern: "",
    username_param: "",
    password_param: "",
    source_code: ""
  });
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [iconEditId, setIconEditId] = useState(null);
  const [iconUrlInput, setIconUrlInput] = useState("");
  const { token } = useAuth();

  const fetchTools = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/tools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTools(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des outils");
    }
  }, [token]);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  const fetchToolConfig = async (toolId) => {
    setLoadingConfig(true);
    try {
      const response = await axios.get(`${API}/admin/tools/${toolId}/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.parser_config) {
        setParserConfig({
          required_path_pattern: response.data.parser_config.required_path_pattern || "",
          username_param: response.data.parser_config.username_param || "",
          password_param: response.data.parser_config.password_param || "",
          source_code: response.data.parser_config.source_code || ""
        });
      }
    } catch (error) {
      console.error("Error fetching tool config");
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      
      // Always include parser_config when editing
      if (editingTool) {
        payload.parser_config = parserConfig;
      }
      
      if (editingTool) {
        await axios.put(`${API}/admin/tools/${editingTool.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Outil mis à jour");
      } else {
        await axios.post(`${API}/admin/tools`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Outil créé");
      }
      setDialogOpen(false);
      setFormData({ name: "", description: "", icon: "wrench", icon_url: "", parser_config: null });
      setParserConfig({ required_path_pattern: "", username_param: "", password_param: "", source_code: "" });
      setEditingTool(null);
      fetchTools();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const toggleTool = async (toolId) => {
    try {
      await axios.post(`${API}/admin/tools/${toolId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTools();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const saveIconUrl = async (toolId, url) => {
    try {
      await axios.put(`${API}/admin/tools/${toolId}`, { icon_url: url || null }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Icône mise à jour");
      setIconEditId(null);
      setIconUrlInput("");
      fetchTools();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour de l'icône");
    }
  };

  const deleteTool = async (toolId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet outil ?")) return;
    try {
      await axios.delete(`${API}/admin/tools/${toolId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTools();
      toast.success("Outil supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const openEditDialog = async (tool) => {
    setEditingTool(tool);
    setFormData({ 
      name: tool.name, 
      description: tool.description || "", 
      icon: tool.icon || "wrench",
      icon_url: tool.icon_url || "",
      parser_config: tool.parser_config || null
    });
    
    // Reset parser config first
    setParserConfig({
      required_path_pattern: "",
      username_param: "",
      password_param: "",
      source_code: ""
    });
    
    // Fetch parser config for all tools
    await fetchToolConfig(tool.id);
    
    setDialogOpen(true);
  };

  // Check if it's an M3u parser tool (needs additional config fields)
  const isM3uParserTool = editingTool?.name?.includes("M3u") && editingTool?.name?.includes("Xstream");

  return (
    <div className="p-6 animate-fade-in" data-testid="tools-management">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Outils</h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingTool(null);
            setFormData({ name: "", description: "", icon: "wrench", icon_url: "", parser_config: null });
            setParserConfig({ required_path_pattern: "", username_param: "", password_param: "", source_code: "" });
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground" data-testid="add-tool-button">
              <Plus className="h-4 w-4 mr-2" />
              Nouvel Outil
            </Button>
          </DialogTrigger>
          <DialogContent className={editingTool ? "max-w-4xl max-h-[90vh] overflow-y-auto" : ""} data-testid="tool-form-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingTool ? (
                  <>
                    <Pencil className="h-5 w-5" />
                    Modifier l'outil : {editingTool.name}
                  </>
                ) : (
                  <>
                    <Plus className="h-5 w-5" />
                    Nouvel outil
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {editingTool ? (
              <Tabs defaultValue="general" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="general" className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Général
                  </TabsTrigger>
                  <TabsTrigger value="code" className="flex items-center gap-2">
                    <Code className="h-4 w-4" />
                    Code Source
                  </TabsTrigger>
                </TabsList>
                
                <form onSubmit={handleSubmit}>
                  <TabsContent value="general" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Nom *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="tool-form-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        data-testid="tool-form-description"
                      />
                    </div>
                    
                    {/* Additional config for M3u parser tools */}
                    {isM3uParserTool && (
                      <div className="p-4 bg-muted/30 border border-border rounded-sm space-y-4">
                        <h4 className="font-semibold text-sm flex items-center gap-2">
                          <Settings className="h-4 w-4 text-primary" />
                          Configuration du Parser M3U
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs">Pattern requis dans l'URL</Label>
                            <Input
                              value={parserConfig.required_path_pattern}
                              onChange={(e) => setParserConfig({ ...parserConfig, required_path_pattern: e.target.value })}
                              placeholder="get.php"
                              className="font-mono text-sm"
                              data-testid="parser-path-pattern"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Paramètre Username</Label>
                            <Input
                              value={parserConfig.username_param}
                              onChange={(e) => setParserConfig({ ...parserConfig, username_param: e.target.value })}
                              placeholder="username"
                              className="font-mono text-sm"
                              data-testid="parser-username-param"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Paramètre Password</Label>
                            <Input
                              value={parserConfig.password_param}
                              onChange={(e) => setParserConfig({ ...parserConfig, password_param: e.target.value })}
                              placeholder="password"
                              className="font-mono text-sm"
                              data-testid="parser-password-param"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="code" className="space-y-4 mt-4">
                    {loadingConfig ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-pulse text-muted-foreground">Chargement...</div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-2">
                            <Code className="h-4 w-4 text-accent" />
                            Code Source / Configuration
                          </Label>
                          <Badge variant="outline" className="text-xs">Lecture / Écriture</Badge>
                        </div>
                        <Textarea
                          value={parserConfig.source_code || ""}
                          onChange={(e) => setParserConfig({ ...parserConfig, source_code: e.target.value })}
                          className="font-mono text-xs h-96 bg-muted/30 border-border"
                          placeholder={`# Code source de l'outil "${editingTool?.name}"\n# Vous pouvez ajouter ici la logique de traitement, des notes,\n# ou tout autre configuration nécessaire.\n\n# Exemple:\n# def process(input_data):\n#     return transformed_data`}
                          data-testid="parser-source-code"
                        />
                        <p className="text-xs text-muted-foreground">
                          Ce code source peut être utilisé pour personnaliser le comportement de l'outil ou documenter sa logique.
                        </p>
                      </div>
                    )}
                  </TabsContent>
                  
                  <DialogFooter className="mt-6">
                    <Button type="submit" data-testid="tool-form-submit">
                      Mettre à jour
                    </Button>
                  </DialogFooter>
                </form>
              </Tabs>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="tool-form-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="tool-form-description"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="tool-form-submit">
                    Créer
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tools.map((tool) => (
          <Card 
            key={tool.id} 
            className={`bg-card border-border transition-all ${
              tool.enabled ? "hover:border-primary/30" : "opacity-50"
            }`}
            data-testid={`tool-card-${tool.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="relative group/icon">
                    <div className={`w-[50px] h-[50px] rounded-sm flex items-center justify-center overflow-hidden ${tool.enabled ? "bg-primary/10" : "bg-muted"}`}>
                      {tool.icon_url ? (
                        <img src={tool.icon_url} alt={tool.name} className="w-full h-full object-cover" />
                      ) : (
                        <Wrench className={`h-6 w-6 ${tool.enabled ? "text-primary" : "text-muted-foreground"}`} />
                      )}
                    </div>
                    <button
                      className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover/icon:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIconEditId(iconEditId === tool.id ? null : tool.id);
                        setIconUrlInput(tool.icon_url || "");
                      }}
                      data-testid={`edit-icon-${tool.id}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  </div>
                  <div>
                    <h3 className="font-semibold">{tool.name}</h3>
                    <p className="text-xs text-muted-foreground">{tool.description}</p>
                  </div>
                </div>
                <Switch
                  checked={tool.enabled}
                  onCheckedChange={() => toggleTool(tool.id)}
                  data-testid={`toggle-tool-${tool.id}`}
                />
              </div>
              
              {iconEditId === tool.id && (
                <div className="mb-3 p-2 bg-muted/30 border border-border rounded-sm space-y-2">
                  <Label className="text-xs flex items-center gap-1">
                    <ImageIcon className="h-3 w-3" /> URL de l'icône (50x50)
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={iconUrlInput}
                      onChange={(e) => setIconUrlInput(e.target.value)}
                      placeholder="https://exemple.com/icon.png"
                      className="text-xs h-8"
                      data-testid={`icon-url-input-${tool.id}`}
                    />
                    <Button size="sm" className="h-8 px-3" onClick={() => saveIconUrl(tool.id, iconUrlInput)}>
                      OK
                    </Button>
                    {tool.icon_url && (
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive" onClick={() => saveIconUrl(tool.id, "")}>
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEditDialog(tool)}
                  data-testid={`edit-tool-${tool.id}`}
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Modifier
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteTool(tool.id)}
                  className="text-destructive hover:bg-destructive/10"
                  data-testid={`delete-tool-${tool.id}`}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Supprimer
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

